import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Award } from "lucide-react";
import { IMAGES } from "@/lib/data";
import { Reveal, MaskedLines, Eyebrow } from "@/components/site/Motion";

const EDUCATION = [
  ["Postgraduate certificate, Aerospace Nursing", "Unyleya · 2024"],
  ["Postgraduate certificate, Emergency and Critical Care (Urgência e Emergência)", "Instituto Brasileiro de Pós-Graduação · 2023"],
  ["Postgraduate certificate, Intensive Care (UTI)", "Instituto Brasileiro de Pós-Graduação · 2023"],
  ["Associate degree, Hospital Management", "UNINOVE · 2023"],
  ["Bachelor of Nursing", "UNINOVE · 2022"],
];

const MEDALS = [
  "Medalha de Mérito Pessoal, Grau 5 — 2017",
  "Medalha de Combatente da Força Pública Paulista, Instituto Histórico Militar — 2020",
  "Medalha de Mérito Pessoal, Grau 4 — 2020",
  "Medalha de Honra ao Mérito Militar — 2021",
  "Medalha de Mérito Pessoal, Grau 3 — 2021",
  "Personal commendation — 2023 COVID-19 and influenza vaccination campaign",
];

const ROLE = [
  "Develops the program architecture",
  "Integrates training components across the five-component framework",
  "Designs joint scenarios and simulation packages",
  "Supports instructor development and the train-the-trainer pathway",
  "Coordinates documentation and corrective-action processes",
];

export default function Architect() {
  return (
    <main data-testid="architect-page">
      {/* HERO WITH PORTRAIT */}
      <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-24 border-b border-line overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_380px] gap-16 items-end">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-xs text-mist">08</span>
              <span className="h-px w-10 bg-signal" />
              <Eyebrow>About the Program Architect</Eyebrow>
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.04]">
              <MaskedLines lines={["Gabriel Boris", "Galvani."]} startDelay={0.1} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="mt-8 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl"
            >
              Tactical Medical Readiness Program Architect and Training Operations Lead.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-6 text-sm text-slate-400 leading-relaxed max-w-xl"
            >
              A Brazilian registered nurse and military police officer whose background bridges frontline law-enforcement operations and emergency/critical-care nursing. He is the architect of T-EMRP, responsible for the program's framework design, curriculum integration, scenario development, and training-operations coordination.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden border border-line">
              <img
                src={IMAGES.architect}
                alt="Portrait of Gabriel Boris Galvani, wearing a navy suit and glasses"
                data-testid="architect-portrait"
                className="w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-signal px-5 py-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white">COREN/SP · PMESP</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <Eyebrow>Nursing and emergency-care background</Eyebrow>
            <p className="mt-6 text-sm text-slate-300 leading-relaxed">
              Registered nurse, licensed by the São Paulo Regional Nursing Council (COREN/SP). Within the São Paulo State Military Police health system, he has served as a primary-care and logistics nurse at a central outpatient clinic, and as an emergency-department and ICU nurse on an extra-duty schedule at the Military Police Hospital. His clinical scope includes emergency and critical-care patient stabilization, hemorrhage control (including tourniquet application), intraosseous access, laryngeal mask airway use, basic and advanced life support, peripherally inserted central catheter (PICC) placement, and primary-care procedures.
            </p>
            <div className="mt-8 border-l-2 border-signal bg-surface p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-3">Licensure note</p>
              <p className="text-sm text-slate-300 leading-relaxed">
                His nursing license (COREN/SP) is a Brazilian credential. It is not presented here as equivalent to any U.S. nursing or clinical license. Any question of U.S. licensure equivalency would require separate documentary review and is outside the scope of this program summary.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="mt-14 font-display font-bold text-xl text-white tracking-tight">Instructional and tactical-medical experience</h3>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              Since February 2024, he has served as a prehospital-care (APH) instructor for the São Paulo State Military Police, teaching hemorrhage control and casualty stabilization techniques to officers responding to high-risk incidents. He holds hands-on clinical course completions in intraosseous access, PICC line placement, advanced life support, hypodermoclysis, laryngeal mask intubation, and hemorrhage control (Stop the Bleed), completed through FABRAC Centro Universitário in 2023.
            </p>
            <h3 className="mt-12 font-display font-bold text-xl text-white tracking-tight">Law-enforcement and logistics background</h3>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              He has served with the São Paulo State Military Police since November 2014, in patrol operations, crowd-management operations, and — from January 2019 to March 2023 — in an internal-affairs role conducting preliminary administrative investigations. Since February 2024, he has also managed unit vehicle-fleet logistics alongside his instructor duties.
            </p>
          </Reveal>
        </div>

        <div>
          <Reveal delay={0.05}>
            <Eyebrow>Education</Eyebrow>
            <div className="mt-6 border border-line divide-y divide-line">
              {EDUCATION.map(([deg, inst], i) => (
                <div key={i} data-testid={`education-${i}`} className="p-5 hover:bg-surface transition-colors">
                  <p className="font-display font-bold text-white tracking-tight text-sm">{deg}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-mist">{inst}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-14 font-mono text-xs uppercase tracking-[0.25em] text-signal">Institutional recognition</p>
            <ul className="mt-6 space-y-3">
              {MEDALS.map((m, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                  <Award size={14} className="text-signal mt-0.5 shrink-0" /> {m}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-mist leading-relaxed">
              These are internal institutional recognitions from his law-enforcement service in Brazil, listed for professional-background context. They are not credentials or endorsements related to T-EMRP.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line bg-surface py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Eyebrow>Proposed role in T-EMRP</Eyebrow>
            <div className="mt-8 border border-line divide-y divide-line bg-ink">
              {ROLE.map((r, i) => (
                <div key={i} data-testid={`role-item-${i}`} className="flex items-center gap-5 p-5 hover:bg-surface transition-colors">
                  <span className="font-mono text-xs text-signal">0{i + 1}</span>
                  <span className="text-sm text-slate-300">{r}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Scope limitations</Eyebrow>
            <p className="mt-8 text-sm text-slate-300 leading-relaxed">
              The Program Architect holds no clinical approval right, no authority over any participating agency's command structure, and no control over the independent evaluation or its findings. All clinical content requires the review and approval of a U.S.-licensed medical director. All operational commitments require the approval of each participating agency's own leadership.
            </p>
            <Link to="/contact" data-testid="architect-cta" className="mt-10 inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
              Request More Information <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
